// status-progress-current-attempt-and-human-actions.ts
//
// Progress panel P1: `progress.current_attempt` (B5 — "file vắng" ≠ "worker
// chết": a worker is dead only when its SUPERVISOR is) and
// `progress.human_actions[]` (B4/B20/X8 — what the operator must do now, each
// with the equivalent CLI command; flags pinned against the real command
// registry by status-progress-human-action-hints-match-cli-registry.test.ts).
// beta.2: every action also carries `hint_chat` — the sentence to say to
// Claude (DF-P4-5) — and a dead supervisor mid-mission is an action (DF-P4-2).
// Pure: liveness and file reads happen in status-progress-facts-reader.ts.
import type { PendingDecisionQuestion } from "./decision-ledger-types.js";
import type { EscalationItem } from "./escalation-trigger-from-journal.js";
import type { GateFile } from "./gate-registry.js";
import { listPendingGateRequests } from "./status-operating-table.js";
import { openAttemptOf, type ProgressJournalFacts } from "./status-progress-journal-facts.js";
import { classifyStatusLabel } from "./status-progress-status-label-classes.js";
import type { ProgressCurrentAttempt, ProgressHumanAction } from "./status-progress-types.js";

export function deriveCurrentAttempt(input: {
  facts: ProgressJournalFacts;
  ticketStatus: Readonly<Record<string, string>>;
  executorOf: ReadonlyMap<string, string>;
  supervisorAlive: boolean;
  /** pid from active-worker.json when the file exists AND that process is alive. */
  liveWorkerPid?: number;
}): ProgressCurrentAttempt | undefined {
  const open = Object.entries(input.ticketStatus)
    .filter(([, status]) => status === "in_progress")
    .map(([ticket]) => openAttemptOf(input.facts, ticket))
    .filter((a) => a !== undefined)
    .sort((a, b) => b.seq - a.seq)[0];
  if (open === undefined) return undefined;
  const executor = input.executorOf.get(open.ticket) ?? "unknown";
  const running =
    input.supervisorAlive && executor !== "controller" && input.liveWorkerPid !== undefined;
  return {
    ticket_id: open.ticket,
    attempt_no: open.attempt_no,
    run_id: open.run_id,
    ...(open.session_id === undefined ? {} : { session_id: open.session_id }),
    ...(open.started_at === undefined ? {} : { started_at: open.started_at }),
    executor,
    ...(running ? { worker_pid: input.liveWorkerPid } : {}),
    worker_state: !input.supervisorAlive
      ? "dead"
      : running
        ? "worker_running"
        : "supervisor_working",
  };
}

/**
 * DF-P4-2: the supervisor died with the mission unfinished — its machine lock
 * is orphaned (`run` refuses with `machine_lock_recovering` until `unlock`),
 * or a ticket is still `in_progress` (its open attempt is `current_attempt`).
 * A clean stop (gate, ceiling) leaves neither. An open attempt on a ticket
 * that is NOT in_progress does not count: a reclaim (`attempt_recovered`)
 * puts the ticket back to todo without ever writing attempt_finished.
 * A mission the operator paused on purpose is not a crash either.
 */
export function supervisorDiedMidMission(input: {
  supervisorAlive: boolean;
  terminal: boolean;
  /** `status_label === "paused"` — the operator stopped it deliberately. */
  paused: boolean;
  /** This mission's machine lock is on disk while its supervisor is not alive. */
  orphanedLock: boolean;
  ticketStatus: Readonly<Record<string, string>>;
}): boolean {
  if (input.supervisorAlive || input.terminal || input.paused) return false;
  return input.orphanedLock || Object.values(input.ticketStatus).includes("in_progress");
}

const MAX_QUESTION_CHARS = 120;
const cli = (missionId: string, rest: string) =>
  `avatar autopilot ${rest.replace("<M>", missionId)}`;

function shorten(text: string): string {
  return text.length <= MAX_QUESTION_CHARS ? text : `${text.slice(0, MAX_QUESTION_CHARS - 1)}…`;
}

/** Same `--text` placeholder rule as `status --decisions` (decision-ledger-status-text.ts). */
function answerValue(q: PendingDecisionQuestion): string {
  if (q.options.length === 0) return "<câu trả lời>";
  return q.options.every((o) => o.id !== undefined) ? "<option id>" : "<nhãn>";
}

function supervisorDeadWording(missionId: string, busyWith: string | undefined) {
  const text = "Supervisor đã dừng đột ngột — mission đang dở";
  if (busyWith === undefined) return { text, hint_chat: `chạy tiếp mission ${missionId}` };
  return {
    text: `${text}; máy đang chạy mission ${busyWith} (unlock sẽ bị từ chối, xử lý mission đó trước)`,
    hint_chat: `máy đang bận mission ${busyWith} — xử lý mission đó trước, rồi chạy tiếp mission ${missionId}`,
  };
}

export function deriveHumanActions(input: {
  missionId: string;
  gates: GateFile;
  pendingDecisions: readonly PendingDecisionQuestion[];
  statusLabel?: string;
  labelStale: boolean;
  /** The standing `escalations[]` status already computes (same journal records). */
  escalations: readonly EscalationItem[];
  /** Blocked sources a live (todo/in_progress) recovery ticket is unblocking. */
  recoveringSources: ReadonlySet<string>;
  /** `supervisorDiedMidMission(...)` — computed by the reader. */
  supervisorDead?: boolean;
  /**
   * Another mission's LIVE supervisor holds the machine lock (the reader passes
   * `liveness.machine_lock_mission_id` only when that pid is alive; a stale foreign
   * lock gets the plain wording): `unlock` for this mission is refused (`refused_other_mission`), so the
   * action still shows but says so (p5d L-1). `hint_cli` stays — it fails safe at `unlock`.
   */
  machineBusyWith?: string;
}): ProgressHumanAction[] {
  const id = input.missionId;
  // Contract addendum (beta.2): supervisor_dead FIRST — the panel keeps this order.
  const actions: ProgressHumanAction[] =
    input.supervisorDead === true
      ? [
          {
            kind: "supervisor_dead",
            ...supervisorDeadWording(id, input.machineBusyWith),
            // `unlock` is a no-op (exit 0) without a lock; `run` refuses until it ran (exit 73).
            hint_cli: `${cli(id, "unlock --mission <M>")} && ${cli(id, "run --mission <M> --detach")}`,
          },
        ]
      : [];
  const gateActions: ProgressHumanAction[] = listPendingGateRequests(input.gates).map((g) => ({
    kind: "gate",
    gate: g.gate,
    ...(g.request_id === undefined ? {} : { request_id: g.request_id }),
    text: `${g.gate.toUpperCase()} chờ duyệt (${g.request_id ?? "?"})`,
    hint_chat: `duyệt ${g.gate.toUpperCase()}`,
    hint_cli: cli(
      id,
      `approve ${g.gate} --mission <M> --request ${g.request_id ?? "<request-id>"}`,
    ),
  }));
  actions.push(...gateActions);
  for (const q of input.pendingDecisions) {
    actions.push({
      kind: "decision",
      question_id: q.questionId,
      ...(q.ticketId === undefined ? {} : { ticket_id: q.ticketId }),
      text: shorten(q.question),
      hint_chat: `chọn phương án … cho ${q.questionId}`,
      hint_cli: cli(id, `answer --mission <M> --question ${q.questionId} --text ${answerValue(q)}`),
    });
  }
  const effect = input.labelStale ? undefined : classifyStatusLabel(input.statusLabel);
  if (effect?.kind === "interview") {
    actions.push({
      kind: "interview",
      text: "trả lời câu hỏi phỏng vấn trong chat",
      hint_chat: "trả lời câu hỏi phỏng vấn",
      hint_cli: cli(id, "answer --mission <M> --question <question-id> --text <giá trị>"),
    });
  }
  if (effect?.kind === "cost_ceiling") {
    actions.push({
      kind: "cost_ceiling",
      text: `mission ${effect.text} — nâng trần trong ~/.avatar/autopilot-config.json rồi chạy lại, hoặc dừng`,
      hint_chat: "nâng trần chi phí mission",
      hint_cli: cli(id, "run --mission <M>"),
    });
  }
  for (const e of input.escalations) {
    if (input.recoveringSources.has(e.ticket_id)) continue;
    actions.push({
      kind: "escalation",
      ticket_id: e.ticket_id,
      blocked_by: e.blocked_by,
      ...(e.recovery_skipped_reason === undefined
        ? {}
        : { recovery_skipped_reason: e.recovery_skipped_reason }),
      text: `ticket ${e.ticket_id} bị chặn (${e.blocked_by}) — cần bạn chỉ hướng`,
      hint_chat: `xem escalation của ${e.ticket_id}`,
      hint_cli: cli(id, 'redirect --mission <M> --text "<chỉ đạo>"'),
    });
  }
  return actions;
}
